import React, { useState } from 'react';
import axios from 'axios';
import { contractorAuth } from '../lib/contractorAuth';
import './HandBack.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * "This one is not mine to fix."
 *
 * Gordon: "allow a contractor to reassign tickets back to the approver (for a
 * given reason such as that he is not able to fix the problem or not within
 * his competency for example)."
 *
 * Every control a contractor had on a ticket moved it forward - a status, some
 * hours, resolve it. A job outside their competency had no route at all except
 * a phone call, so it sat in their queue looking exactly like work in
 * progress, which is the worst state a ticket can be in: indistinguishable
 * from a ticket somebody is handling.
 *
 * SHUT BY DEFAULT, AND NOT A BUTTON THAT FIRES. Handing a ticket back takes
 * it off this person and puts it in the office's queue, and it cannot be
 * undone from here. A single click would do that by accident on a phone, so
 * the control opens a box, the reason is required, and the confirm says what
 * it will do rather than "OK".
 *
 * THE REASON IS THE POINT, not paperwork. "Not within my competency" and
 * "customer cancelled" lead to completely different decisions, and a
 * reassignment with the reason thrown away sends the next contractor into the
 * same wall. The server requires it too - ticket_handbacks has a CHECK on it -
 * so this is not the only thing standing between a blank reason and the
 * database.
 */
const SUGGESTIONS = [
  'Not within my competency',
  'I cannot get the part',
  'Needs somebody on site, and I cannot travel',
  'Customer has cancelled'
];

const HandBack = ({ ticket, onDone }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [blocked, setBlocked] = useState(null);

  // A settled ticket is read-only, and the server says 409. Not offering the
  // control is better than offering one that is refused: the route back from
  // a signed-off ticket is a reopen request, which lives elsewhere.
  const settled = ['Approved', 'Closed'].includes(String(ticket?.status || ''));
  if (settled) return null;

  const send = async () => {
    const text = reason.trim();
    if (!text) { setError('Say why, so the next person is not sent into the same wall'); return; }
    setBusy(true);
    setError(null);
    try {
      // contractorAuth() is undefined for a member of staff, so their request
      // goes through the axios interceptor and picks up the staff token - an
      // administrator can hand a ticket back on somebody's behalf, which is
      // how a ticket comes off a contractor who has left.
      await axios.post(`${API_BASE}/tickets/${ticket.id}/handbacks`,
        { reason: text }, contractorAuth());
      setOpen(false);
      setReason('');
      if (onDone) onDone();
    } catch (err) {
      // A 428 is the location gate and has a panel of its own with a way out
      // of it; anything else is a sentence.
      if (err.response?.status === 428) {
        setBlocked(err);
        setError(null);
      } else {
        setError(err.response?.data?.detail
          || err.response?.data?.error
          || 'Could not hand that ticket back');
      }
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <section className="hb">
        <button type="button" className="hb-open" onClick={() => setOpen(true)}>
          Hand back for reassignment
        </button>
        <p className="hb-why">
          If this job is not something you can do, give it back and say why. It
          goes to the office to be reassigned.
        </p>
      </section>
    );
  }

  return (
    <section className="hb hb-is-open">
      <h4 className="hb-head">Hand this ticket back</h4>
      <p className="hb-why">
        It comes off you straight away and goes into the office&rsquo;s queue to
        be given to somebody else. You cannot undo this yourself.
      </p>

      <label className="hb-label" htmlFor={`hb-reason-${ticket.id}`}>
        Why are you handing it back?
      </label>
      <textarea
        id={`hb-reason-${ticket.id}`}
        className="hb-box"
        rows={3}
        value={reason}
        maxLength={2000}
        placeholder="What stopped you?"
        onChange={(e) => setReason(e.target.value)}
        disabled={busy}
      />

      <ul className="hb-quick">
        {SUGGESTIONS.map((sug) => (
          <li key={sug}>
            <button type="button" className="hb-chip" disabled={busy}
              onClick={() => setReason(sug)}>
              {sug}
            </button>
          </li>
        ))}
      </ul>

      {/* A 428 is explained by <LocationGate /> in the shell - once, for
          every screen - so this says only that the hand-back did not happen
          and leaves the box open with the reason still typed in it. */}
      {blocked && (
        <p className="hb-error" role="alert">
          Not handed back yet: this change needs your location. See the panel
          in the corner.
        </p>
      )}
      {error && <p className="hb-error" role="alert">{error}</p>}

      <div className="hb-row">
        <button type="button" className="hb-go" onClick={send}
          disabled={busy || !reason.trim()}>
          {busy ? 'Handing back…' : 'Hand back to the office'}
        </button>
        <button type="button" className="hb-cancel" disabled={busy}
          onClick={() => { setOpen(false); setError(null); }}>
          Keep it
        </button>
      </div>
    </section>
  );
};

export default HandBack;
